import { CalendarDays, History, LogOut } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Wordmark } from "./wordmark";

interface Tab {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    to: "/chore-schedules",
    label: "Schedules",
    icon: <CalendarDays size={24} />,
  },
  {
    to: "/chore-history",
    label: "History",
    icon: <History size={24} />,
  },
];

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string | undefined;
}

export function AppLayout({ children, title }: AppLayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentTab = tabs.find((tab) => pathname.startsWith(tab.to));

  async function handleLogout() {
    await authClient.signOut();
    navigate("/login");
  }

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border px-4 py-3">
        <Wordmark variant="small" />
        {(title ?? currentTab) && (
          <span className="text-sm font-medium text-muted-foreground">
            {title ?? currentTab?.label}
          </span>
        )}
      </header>
      <main
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: "calc(4rem + env(safe-area-inset-bottom))" }}
      >
        {children}
      </main>
      <nav
        className="fixed bottom-0 inset-x-0 bg-background border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="flex">
          {tabs.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                className={({ isActive }) =>
                  cn(
                    buttonVariants({ variant: "ghost" }),
                    "flex h-auto w-full flex-col items-center gap-1 py-2 text-xs font-medium",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {tab.icon}
                {tab.label}
              </NavLink>
            </li>
          ))}
          <li className="flex-1">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="flex h-auto w-full flex-col items-center gap-1 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <LogOut size={24} />
              Logout
            </Button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
